import { format } from "date-fns";
import { Clock, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Event } from "@/hooks/useEvents";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const eventTypeColors = {
  meeting: 'bg-blue-500 border-blue-600 text-white',
  consultation: 'bg-green-500 border-green-600 text-white',
  site_visit: 'bg-orange-500 border-orange-600 text-white',
  follow_up: 'bg-purple-500 border-purple-600 text-white',
  presentation: 'bg-red-500 border-red-600 text-white',
  other: 'bg-gray-500 border-gray-600 text-white',
};

interface EventBlockProps {
  event: Event;
  top: number;
  height: number;
  width?: number;
  left?: number;
  onClick: (event: Event) => void;
  viewType: "day" | "3day" | "week";
}

export function EventBlock({
  event,
  top,
  height,
  width = 100,
  left = 0,
  onClick,
  viewType,
}: EventBlockProps) {
  const colorClass = eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other;
  const showDetails = viewType === "day" && height > 40;
  const showTime = (viewType === "day" || viewType === "3day") && height > 25;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "absolute rounded-md border-l-4 p-1 cursor-pointer overflow-hidden transition-all hover:shadow-md hover:z-10",
              colorClass,
              "min-h-[44px]"
            )}
            style={{
              top: `${top}px`,
              height: `${Math.max(height, 44)}px`,
              width: `${width}%`,
              left: `${left}%`,
            }}
            onClick={() => onClick(event)}
          >
            <div className="text-xs sm:text-[11px] font-medium truncate">
              {event.title}
            </div>
            {showTime && (
              <div className="flex items-center text-[10px] opacity-90 mt-0.5">
                <Clock className="h-2.5 w-2.5 mr-0.5" />
                {format(new Date(event.start_time), 'h:mm a')}
              </div>
            )}
            {showDetails && event.location && (
              <div className="flex items-center text-[10px] opacity-90 mt-0.5 truncate">
                <MapPin className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
                {event.location}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">{event.title}</p>
            <p className="text-xs">
              {format(new Date(event.start_time), 'h:mm a')} - {format(new Date(event.end_time), 'h:mm a')}
            </p>
            {event.location && (
              <p className="text-xs">{event.location}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
